import { Dialog, DialogContent, Box, Typography, Divider, TextField, DialogActions, Button } from '@mui/material';
import { CloseButton } from './CloseButton';
import { CancelButton } from './CancelButton';
import { Notification, NOTIFICATION_TARGET_TYPES, NOTIFICATION_STATUS, User, Enterprise, USER_COLLECTION, ENTERPRISE_COLLECTION } from '../type';
import { formatDate } from '../utils/dateUtils';
import { UserSelection } from './UserSelection';
import { useEffect, useState } from 'react';
import { collection, getFirestore, onSnapshot, orderBy, query } from 'firebase/firestore';

interface NotificationDetailDialogProps {
    notification: Notification | null;
    onClose: () => void;
    handleDelete: (notificationId?: string) => Promise<void>;
}

export const NotificationDetailDialog = ({ notification, onClose, handleDelete }: NotificationDetailDialogProps) => {
    const [independentUsers, setIndependentUsers] = useState<User[]>([]);
    const [selectedIndependentUsers, setSelectedIndependentUsers] = useState<User[]>([]);
    const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
    const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
    const [selectedEnterpriseUsers, setSelectedEnterpriseUsers] = useState<User[]>([]);

    const getTargetTypeLabel = (targetType?: Notification['targetType']) => {
        switch (targetType) {
            case NOTIFICATION_TARGET_TYPES.ALL:
                return '전체 사용자';
            case NOTIFICATION_TARGET_TYPES.TOKEN_LIST:
                return '선택 사용자';
            case NOTIFICATION_TARGET_TYPES.APPROVED_OPTIONAL_USERS:
                return '승인 + 선택약관 동의 사용자';
            default:
                return targetType || '-';
        }
    };
    
    useEffect(() => {
        const db = getFirestore();
        const usersRef = collection(db, USER_COLLECTION);
        const q = query(usersRef, orderBy('createdAt', 'desc'));

        onSnapshot(q, (snapshot) => {
            const i_users: User[] = [];
            const e_users: User[] = [];

            snapshot.forEach(doc => {
                const data = doc.data() as User;
                if (data.uid?.length > 0 && data.displayName?.length > 0) {
                    if (data.isEnterpriseUser && data.enterpriseId) {
                    e_users.push(data);
                    } else {
                    i_users.push(data);
                    }
                }
            });

            setIndependentUsers(i_users);
            setEnterpriseUsers(e_users);
        });

        const enterprisesRef = collection(db, ENTERPRISE_COLLECTION);
        const eq = query(enterprisesRef, orderBy('createdAt', 'desc'));

        onSnapshot(eq, (snapshot) => {
            const list: Enterprise[] = [];

            snapshot.docs.forEach(doc => {
                const data = doc.data() as Enterprise;
                list.push(data);
            });
            setEnterprises(list);
        });
    }, []);
    
    useEffect(() => {
        if (notification) {
            setSelectedIndependentUsers(independentUsers.filter(user => 
                notification.targetUsers && Array.isArray(notification.targetUsers) && user.isEnterpriseUser === false
                    ? notification.targetUsers?.includes?.(user.uid)
                    : false
            ));
            setSelectedEnterpriseUsers(enterpriseUsers.filter(user => 
                notification.targetUsers && Array.isArray(notification.targetUsers) && user.isEnterpriseUser === true
                    ? notification.targetUsers?.includes?.(user.uid)
                    : false
            ));
            // setEnterprises(
            //     notification?.targetEnterprises && Array.isArray(notification.targetEnterprises)
            //         ? enterprises.filter(enterprise => notification.targetEnterprises?.includes?.(enterprise.enterpriseId))
            //         : enterprises
            // );

        }
    }, [notification, independentUsers, enterpriseUsers]);
    
    return (
        <Dialog
            open={!!notification}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            {/* 상단 닫기 버튼 및 제목 */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 3, pb: 2 }}>
                <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>
                    알림 상세 정보
                </Typography>
                <CloseButton onClose={onClose} />
            </Box>
            <Divider />
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
                    {notification && (
                        <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="알림 ID"
                                value={notification.notificationId}
                                slotProps={{ input: { readOnly: true } }}
                                fullWidth
                            />
                            <TextField
                                label="제목"
                                value={notification.title}
                                slotProps={{ input: { readOnly: true } }}
                                fullWidth
                            />
                            <TextField
                                label="내용"
                                value={notification.body}
                                slotProps={{ input: { readOnly: true } }}
                                multiline
                                rows={3}
                                fullWidth
                            />

                            <Box sx={{
                                width: '100%', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center',
                                border: '1px solid',
                                borderColor: 'grey.300',
                                borderRadius: 2,
                                p: 2, 
                                mb: 2,
                                position: 'relative',
                            }}>
                                <Typography
                                variant="subtitle2"
                                sx={{
                                    position: 'absolute',
                                    top: -12,
                                    left: 16,
                                    backgroundColor: 'background.paper',
                                    px: 1,
                                }}
                                >
                                연결화면
                                </Typography>
                                {/* <Button
                                    variant="outlined"
                                    onClick={handleSelectContent}
                                    fullWidth
                                    sx={{ mb: 1 }}
                                >
                                    홈/이벤트/공지사항/게시물/설정 선택
                                </Button> */}

                                {notification.content ? (
                                    <Box sx={{ width: '100%', mt: 1, textAlign: 'left' }}>
                                        <Typography variant="body2">선택된 연결화면:</Typography>
                                        <Typography variant="body2">- 타입: {notification.content?.type}</Typography>
                                        {notification.content?.id && (
                                            <Typography variant="body2">- ID: {notification.content?.id}</Typography>
                                        )}
                                        {notification.content?.title && (
                                            <Typography variant="body2">- 제목: {notification.content?.title}</Typography>
                                        )}
                                        <Typography variant="body2">- 상세내용: {notification.content?.content}</Typography>
                                        {/* <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            sx={{ mt: 1 }}
                                            onClick={() => setNotificationData((prev) => ({ ...prev, content: null }))}
                                        >
                                            선택 취소
                                        </Button> */}
                                    </Box>
                                    ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        선택된 연결화면이 없습니다.
                                    </Typography>
                                )}
                            </Box>

                            <TextField
                                label="발송 대상"
                                value={getTargetTypeLabel(notification.targetType)}
                                slotProps={{ input: { readOnly: true } }}
                                fullWidth
                            />
                            <TextField
                                label="상태"
                                value={
                                    notification.status === NOTIFICATION_STATUS.PENDING ? '대기중' :
                                    notification.status === NOTIFICATION_STATUS.SENT ? '발송됨' :
                                    notification.status === NOTIFICATION_STATUS.FAILED ? '실패' :
                                    notification.status
                                }
                                slotProps={{ input: { readOnly: true } }}
                                fullWidth
                            />
                            <TextField
                                label="예약 발송 시각"
                                value={formatDate(notification.scheduledAt)}
                                slotProps={{ input: { readOnly: true } }}
                                fullWidth
                            />
                            <TextField
                                label="발송/실패 시각"
                                value={(notification.status === NOTIFICATION_STATUS.SENT && notification.sentAt) ? formatDate(notification.sentAt) : (notification.status === NOTIFICATION_STATUS.FAILED && notification.failedAt) ? formatDate(notification.failedAt) : '-'}
                                slotProps={{ input: { readOnly: true } }}
                                fullWidth
                            />
                            <TextField
                                label="생성일"
                                value={formatDate(notification.createdAt)}
                                slotProps={{ input: { readOnly: true } }}
                                fullWidth
                            />
                        </Box>
                        {notification.targetType !== NOTIFICATION_TARGET_TYPES.APPROVED_OPTIONAL_USERS && (
                            <UserSelection
                                title="대상 선택"
                                isChecked={notification.targetType === NOTIFICATION_TARGET_TYPES.ALL}
                                selectedEnterpriseUsers={selectedEnterpriseUsers}
                                setSelectedEnterpriseUsers={setSelectedEnterpriseUsers}
                                selectedIndependentUsers={selectedIndependentUsers}
                                setSelectedIndependentUsers={setSelectedIndependentUsers}
                                enterpriseUsers={enterpriseUsers}
                                setEnterpriseUsers={setEnterpriseUsers}
                                independentUsers={independentUsers}
                                setIndependentUsers={setIndependentUsers}
                                enterprises={enterprises}
                            />
                        )}
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions
                sx={{ px: 3, pb: 2, pt: 1,
                    display: 'flex',
                    justifyContent: 'space-between'
                }}>
                <Button 
                    onClick={() => handleDelete(notification?.notificationId)}
                    sx={{ 
                        backgroundColor: '#EC2D4D',
                        color: '#FFF',
                        minWidth: 120,
                        fontWeight: 500,
                        '&:hover': {
                            backgroundColor: '#F26C82'
                        },
                        '&.Mui-disabled': {
                            color: '#FFF',
                            backgroundColor: '#EC2D4D', // Optional: keep same background when disabled
                            opacity: 0.6, // Optional: visually indicate disabled state
                        },
                    }}
                    disabled={notification?.status === NOTIFICATION_STATUS.SENT}
                >
                    삭제
                </Button>
                <CancelButton onClick={onClose}>닫기</CancelButton>
            </DialogActions>
        </Dialog>
    );
}; 
